import * as React from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TerrainRoundedIcon from '@mui/icons-material/TerrainRounded'
import {
    getAttackSetupAssignment,
    getSavedAttackTemplateReference,
    syncAttackTemplateSelection
} from './attackTemplateAssignments'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'
import {
    isCampBotEnabled,
    resolveCampBotTargetType
} from './FunctionSelectionPanel'

const ATTACK_PLUGIN_KEY = 'attack'
const NOMAD_PLUGIN_KEY = 'attacknomads'
const SAMURAI_PLUGIN_KEY = 'attacksams'
const SETTINGS_KEY = 'campBotSettings'
const CAMP_TARGET_SLOT_COUNT = 4
const CLASSIC_SAMURAI_DIFFICULTY = '-1'
const DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT = '1'
const MAX_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT = 20
const DEFAULT_ADVISOR_SETTINGS = Object.freeze({
    enabled: false,
    attackCount: '10',
    sendBattleReports: true,
    autoActivate: true,
    autoSkipCooldown: true
})
const TARGET_TYPES = Object.freeze({
    nomad: 'nomad',
    samurai: 'samurai'
})

const SAMURAI_MODES = Object.freeze({
    camps: 'camps',
    daimyo: 'daimyo',
    both: 'both'
})

const KINGDOM_OPTIONS = Object.freeze([
    { value: '0', label: 'Green' },
    { value: '1', label: 'Ice' },
    { value: '2', label: 'Desert' },
    { value: '3', label: 'Fire' }
])

const TIME_SKIP_OPTIONS = Object.freeze([
    { value: '1', label: '1 Minute' },
    { value: '5', label: '5 Minutes' },
    { value: '10', label: '10 Minutes' },
    { value: '30', label: '30 Minutes' },
    { value: '60', label: '1 Hour' },
    { value: '300', label: '5 Hours' },
    { value: '1440', label: '24 Hours' }
])

const SAMURAI_DIFFICULTY_OPTIONS = Object.freeze([
    { value: CLASSIC_SAMURAI_DIFFICULTY, label: 'Classic' },
    { value: '0', label: 'Easy' },
    { value: '1', label: 'Easy+' },
    { value: '2', label: 'Intermediate' },
    { value: '3', label: 'Intermediate+' },
    { value: '4', label: 'Hard' },
    { value: '5', label: 'Hard+' },
    { value: '6', label: 'Expert' },
    { value: '7', label: 'Expert+' },
    { value: '8', label: 'Master' },
    { value: '9', label: 'Master+' },
    { value: '10', label: 'Archmaster' }
])

const EMPTY_TEMPLATE = Object.freeze({
    templateId: '',
    name: '',
    ownerUsername: '',
    description: '',
    template: null
})

const createEmptyCampTargets = () =>
    Array.from({ length: CAMP_TARGET_SLOT_COUNT }, () => ({ x: '', y: '' }))

const createDefaultSamuraiModeSettings = (overrides = {}) => ({
    campTargets: createEmptyCampTargets(),
    attackTemplate: EMPTY_TEMPLATE,
    ...overrides
})

const DEFAULT_EVENT_SETTINGS = Object.freeze({
    campTargets: createEmptyCampTargets(),
    useFeathers: true,
    primaryTimeSkipType: '30',
    primaryTimeSkipAmount: '3',
    secondaryTimeSkipType: '60',
    secondaryTimeSkipAmount: '1',
    advisor: DEFAULT_ADVISOR_SETTINGS
})

const DEFAULT_SETTINGS = Object.freeze({
    enabled: false,
    attackCountThreshold: '1000',
    senderX: '0',
    senderY: '0',
    targetType: TARGET_TYPES.nomad,
    kingdomId: '0',
    slowestUnitSpeed: '28',
    campAttackTemplate: EMPTY_TEMPLATE,
    nomads: {
        ...DEFAULT_EVENT_SETTINGS
    },
    samurai: {
        ...DEFAULT_EVENT_SETTINGS,
        mode: SAMURAI_MODES.camps,
        difficulty: '3',
        camps: createDefaultSamuraiModeSettings(),
        daimyo: createDefaultSamuraiModeSettings({
            attackTrainCount: DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT
        })
    }
})

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const isPlainObject = value =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const toStringValue = (value, fallback = '') =>
    [undefined, null].includes(value) ? fallback : String(value)

const normalizeClassicDaimyoAttackTrainCount = (value, fallback = DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT) => {
    const parsed = Math.floor(Number(value))
    const fallbackParsed = Math.floor(Number(fallback))
    const resolved = Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackParsed
    if (!Number.isFinite(resolved) || resolved <= 0)
        return DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT

    return String(Math.min(resolved, MAX_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT))
}

const normalizeTemplate = value => ({
    templateId: [undefined, null, ''].includes(value?.templateId) ? '' : String(value.templateId),
    name: toStringValue(value?.name),
    ownerUsername: toStringValue(value?.ownerUsername),
    description: toStringValue(value?.description),
    template: value?.template && typeof value.template === 'object' ? cloneValue(value.template) : null
})

const normalizeCampTargets = (value, fallbackTarget = { x: '', y: '' }) => {
    const entries = Array.isArray(value) ? value : []
    const normalizedEntries = entries
        .slice(0, CAMP_TARGET_SLOT_COUNT)
        .map(entry => ({
            x: toStringValue(entry?.x ?? entry?.[0]),
            y: toStringValue(entry?.y ?? entry?.[1])
        }))

    while (normalizedEntries.length < CAMP_TARGET_SLOT_COUNT)
        normalizedEntries.push({ x: '', y: '' })

    if (!normalizedEntries.some(entry => entry.x || entry.y) && (fallbackTarget.x || fallbackTarget.y))
        normalizedEntries[0] = { x: toStringValue(fallbackTarget.x), y: toStringValue(fallbackTarget.y) }

    return normalizedEntries
}

const parseTargetCoords = value => {
    if (!value || typeof value !== 'string')
        return { x: '', y: '' }

    const parts = value.trim().split(/[:\s,;]+/)
    const x = Number(parts[0])
    const y = Number(parts[1])

    return {
        x: Number.isFinite(x) ? String(Math.trunc(x)) : '',
        y: Number.isFinite(y) ? String(Math.trunc(y)) : ''
    }
}


const resolveSamuraiMode = (source, userPlugins) => {
    const storedMode = String(source?.mode ?? '').trim().toLowerCase()

    if (storedMode === SAMURAI_MODES.both)
        return SAMURAI_MODES.both

    if (storedMode === SAMURAI_MODES.daimyo)
        return SAMURAI_MODES.daimyo

    if (storedMode === SAMURAI_MODES.camps)
        return SAMURAI_MODES.camps

    if (Boolean(source?.classicDaimyo32SoldiersMode ?? userPlugins?.[SAMURAI_PLUGIN_KEY]?.classicDaimyo32SoldiersMode))
        return SAMURAI_MODES.daimyo

    return SAMURAI_MODES.camps
}

const createSectionSource = (rootSource, sectionKey) => {
    const baseSource = isPlainObject(rootSource) ? rootSource : {}
    const nestedSource = isPlainObject(baseSource?.[sectionKey]) ? baseSource[sectionKey] : {}
    return {
        ...baseSource,
        ...nestedSource
    }
}

const normalizeEventSection = (sectionSource, fallbackTarget, defaults) => ({
    campTargets: normalizeCampTargets(sectionSource.campTargets, {
        x: toStringValue(sectionSource.campX, fallbackTarget.x),
        y: toStringValue(sectionSource.campY, fallbackTarget.y)
    }),
    useFeathers: Boolean(sectionSource.useFeathers ?? defaults.useFeathers),
    primaryTimeSkipType: toStringValue(sectionSource.primaryTimeSkipType, defaults.primaryTimeSkipType),
    primaryTimeSkipAmount: toStringValue(sectionSource.primaryTimeSkipAmount, defaults.primaryTimeSkipAmount),
    secondaryTimeSkipType: toStringValue(sectionSource.secondaryTimeSkipType, defaults.secondaryTimeSkipType),
    secondaryTimeSkipAmount: toStringValue(sectionSource.secondaryTimeSkipAmount, defaults.secondaryTimeSkipAmount),
    advisor: {
        enabled: Boolean(sectionSource.advisor?.enabled ?? DEFAULT_ADVISOR_SETTINGS.enabled),
        attackCount: toStringValue(sectionSource.advisor?.attackCount, DEFAULT_ADVISOR_SETTINGS.attackCount),
        sendBattleReports: Boolean(sectionSource.advisor?.sendBattleReports ?? DEFAULT_ADVISOR_SETTINGS.sendBattleReports),
        autoActivate: Boolean(sectionSource.advisor?.autoActivate ?? DEFAULT_ADVISOR_SETTINGS.autoActivate),
        autoSkipCooldown: Boolean(sectionSource.advisor?.autoSkipCooldown ?? DEFAULT_ADVISOR_SETTINGS.autoSkipCooldown)
    }
})

const normalizeSamuraiModeSection = (samuraiSource, modeKey, fallbackTarget, fallbackTemplate, modeDefaults = {}) => {
    const modeSource = createSectionSource(samuraiSource, modeKey)
    const normalizedSection = {
        campTargets: normalizeCampTargets(modeSource.campTargets, {
            x: toStringValue(modeSource.campX, fallbackTarget.x),
            y: toStringValue(modeSource.campY, fallbackTarget.y)
        }),
        attackTemplate: normalizeTemplate(modeSource.attackTemplate ?? fallbackTemplate),
        primaryTimeSkipType: toStringValue(modeSource.primaryTimeSkipType, DEFAULT_EVENT_SETTINGS.primaryTimeSkipType),
        primaryTimeSkipAmount: toStringValue(modeSource.primaryTimeSkipAmount, DEFAULT_EVENT_SETTINGS.primaryTimeSkipAmount),
        secondaryTimeSkipType: toStringValue(modeSource.secondaryTimeSkipType, DEFAULT_EVENT_SETTINGS.secondaryTimeSkipType),
        secondaryTimeSkipAmount: toStringValue(modeSource.secondaryTimeSkipAmount, DEFAULT_EVENT_SETTINGS.secondaryTimeSkipAmount)
    }

    if (
        Object.prototype.hasOwnProperty.call(modeDefaults, 'attackTrainCount') ||
        modeSource.attackTrainCount !== undefined ||
        modeSource.classicDaimyoAttackTrainCount !== undefined
    ) {
        normalizedSection.attackTrainCount = normalizeClassicDaimyoAttackTrainCount(
            modeSource.attackTrainCount ?? modeSource.classicDaimyoAttackTrainCount,
            modeDefaults.attackTrainCount ?? DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT
        )
    }

    return normalizedSection
}

export const normalizeCampBotSettings = userPlugins => {
    const attackPluginState = userPlugins?.[ATTACK_PLUGIN_KEY]
    const source = isPlainObject(attackPluginState?.[SETTINGS_KEY]) ? attackPluginState[SETTINGS_KEY] : {}
    const nomadSource = createSectionSource(source, 'nomads')
    const samuraiSource = createSectionSource(source, 'samurai')
    const campTemplateAssignment = getAttackSetupAssignment(userPlugins, 'camp')
    const sharedCampTemplate = getSavedAttackTemplateReference(attackPluginState, campTemplateAssignment)
    const samuraiTemplate = getSavedAttackTemplateReference(userPlugins?.[SAMURAI_PLUGIN_KEY], null)
    const nomadFallbackTarget = parseTargetCoords(userPlugins?.[NOMAD_PLUGIN_KEY]?.targetCoords)
    const samuraiFallbackTarget = parseTargetCoords(userPlugins?.[SAMURAI_PLUGIN_KEY]?.targetCoords)
    const samuraiCampMode = normalizeSamuraiModeSection(
        samuraiSource,
        SAMURAI_MODES.camps,
        samuraiFallbackTarget,
        samuraiTemplate
    )
    const samuraiDaimyoMode = normalizeSamuraiModeSection(
        samuraiSource,
        SAMURAI_MODES.daimyo,
        samuraiFallbackTarget,
        samuraiTemplate,
        {
            attackTrainCount: toStringValue(
                userPlugins?.[SAMURAI_PLUGIN_KEY]?.classicDaimyoAttackTrainCount,
                DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT
            )
        }
    )

    return {
        enabled: isCampBotEnabled(userPlugins),
        attackCountThreshold: toStringValue(source.attackCountThreshold, DEFAULT_SETTINGS.attackCountThreshold),
        senderX: toStringValue(source.senderX, DEFAULT_SETTINGS.senderX),
        senderY: toStringValue(source.senderY, DEFAULT_SETTINGS.senderY),
        targetType: resolveCampBotTargetType(userPlugins),
        kingdomId: toStringValue(source.kingdomId, DEFAULT_SETTINGS.kingdomId),
        slowestUnitSpeed: toStringValue(source.slowestUnitSpeed, DEFAULT_SETTINGS.slowestUnitSpeed),
        campAttackTemplate: normalizeTemplate(source.attackTemplate ?? sharedCampTemplate),
        nomads: {
            ...normalizeEventSection(nomadSource, nomadFallbackTarget, {
                ...DEFAULT_EVENT_SETTINGS,
                useFeathers: Boolean(
                    nomadSource.useFeathers ??
                    source.useFeathers ??
                    userPlugins?.[NOMAD_PLUGIN_KEY]?.useLeather ??
                    DEFAULT_EVENT_SETTINGS.useFeathers
                )
            })
        },
        samurai: {
            ...normalizeEventSection(samuraiSource, samuraiFallbackTarget, {
                ...DEFAULT_EVENT_SETTINGS,
                useFeathers: Boolean(
                    samuraiSource.useFeathers ??
                    source.useFeathers ??
                    userPlugins?.[SAMURAI_PLUGIN_KEY]?.useLeather ??
                    DEFAULT_EVENT_SETTINGS.useFeathers
                )
            }),
            mode: resolveSamuraiMode(samuraiSource, userPlugins),
            difficulty: toStringValue(
                samuraiSource.difficulty ?? userPlugins?.[SAMURAI_PLUGIN_KEY]?.eventDifficulty,
                DEFAULT_SETTINGS.samurai.difficulty
            ),
            camps: samuraiCampMode,
            daimyo: samuraiDaimyoMode
        }
    }
}

export const buildCampBotPluginState = (userPlugins, nextConfig) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    const enabled = Boolean(nextConfig.enabled ?? isCampBotEnabled(nextPlugins))
    const targetType = nextConfig.targetType === TARGET_TYPES.samurai ? TARGET_TYPES.samurai : TARGET_TYPES.nomad
    const nextSamuraiCampConfig =
        isPlainObject(nextConfig.samurai?.camps) ?
            nextConfig.samurai.camps :
            {
                campTargets: nextConfig.samurai?.campTargets,
                attackTemplate: nextConfig.samurai?.attackTemplate
            }
    const nextSamuraiDaimyoConfig =
        isPlainObject(nextConfig.samurai?.daimyo) ?
            nextConfig.samurai.daimyo :
            {
                campTargets: nextConfig.samurai?.campTargets,
                attackTemplate: nextConfig.samurai?.attackTemplate
            }
    const normalizedDaimyoTrainCount = normalizeClassicDaimyoAttackTrainCount(
        nextSamuraiDaimyoConfig.attackTrainCount
    )
    const normalizedNomadTargets = normalizeCampTargets(nextConfig.nomads.campTargets)
    const normalizedSamuraiCampTargets = normalizeCampTargets(nextSamuraiCampConfig.campTargets)
    const normalizedSamuraiDaimyoTargets = normalizeCampTargets(nextSamuraiDaimyoConfig.campTargets)
    const primaryNomadTarget = getPrimaryFilledTarget(normalizedNomadTargets)
    const primarySamuraiCampTarget = getPrimaryFilledTarget(normalizedSamuraiCampTargets)
    const primarySamuraiDaimyoTarget = getPrimaryFilledTarget(normalizedSamuraiDaimyoTargets)
    const activeSamuraiTargets =
        nextConfig.samurai.mode === SAMURAI_MODES.daimyo ?
            normalizedSamuraiDaimyoTargets :
            normalizedSamuraiCampTargets
    const activeSamuraiPrimaryTarget =
        nextConfig.samurai.mode === SAMURAI_MODES.daimyo ?
            primarySamuraiDaimyoTarget :
            primarySamuraiCampTarget
    const normalizedSamuraiCampTemplate = normalizeTemplate(nextSamuraiCampConfig.attackTemplate)
    const normalizedSamuraiDaimyoTemplate = normalizeTemplate(nextSamuraiDaimyoConfig.attackTemplate)
    const activeSamuraiTemplate =
        nextConfig.samurai.mode === SAMURAI_MODES.daimyo ?
            normalizedSamuraiDaimyoTemplate :
            normalizedSamuraiCampTemplate
    const activeTargets =
        targetType === TARGET_TYPES.samurai ? activeSamuraiTargets : normalizedNomadTargets
    const activePrimaryTarget =
        targetType === TARGET_TYPES.samurai ? activeSamuraiPrimaryTarget : primaryNomadTarget

    nextPlugins[ATTACK_PLUGIN_KEY] ??= {}
    nextPlugins[NOMAD_PLUGIN_KEY] ??= {}
    nextPlugins[SAMURAI_PLUGIN_KEY] ??= {}
    delete nextPlugins.attackbladecoast

    nextPlugins[ATTACK_PLUGIN_KEY].state = enabled
    nextPlugins[ATTACK_PLUGIN_KEY][SETTINGS_KEY] = {
        enabled,
        attackCountThreshold: nextConfig.attackCountThreshold,
        senderX: nextConfig.senderX,
        senderY: nextConfig.senderY,
        targetType,
        kingdomId: nextConfig.kingdomId,
        slowestUnitSpeed: nextConfig.slowestUnitSpeed,
        attackTemplate: normalizeTemplate(nextConfig.campAttackTemplate),
        campX: activePrimaryTarget.x,
        campY: activePrimaryTarget.y,
        campTargets: activeTargets,
        useFeathers:
            targetType === TARGET_TYPES.samurai ? nextConfig.samurai.useFeathers : nextConfig.nomads.useFeathers,
        primaryTimeSkipType:
            targetType === TARGET_TYPES.samurai ? nextConfig.samurai.primaryTimeSkipType : nextConfig.nomads.primaryTimeSkipType,
        primaryTimeSkipAmount:
            targetType === TARGET_TYPES.samurai ? nextConfig.samurai.primaryTimeSkipAmount : nextConfig.nomads.primaryTimeSkipAmount,
        secondaryTimeSkipType:
            targetType === TARGET_TYPES.samurai ? nextConfig.samurai.secondaryTimeSkipType : nextConfig.nomads.secondaryTimeSkipType,
        secondaryTimeSkipAmount:
            targetType === TARGET_TYPES.samurai ? nextConfig.samurai.secondaryTimeSkipAmount : nextConfig.nomads.secondaryTimeSkipAmount,
        nomads: {
            ...nextConfig.nomads,
            campX: primaryNomadTarget.x,
            campY: primaryNomadTarget.y,
            campTargets: normalizedNomadTargets
        },
        samurai: {
            ...nextConfig.samurai,
            campX: activeSamuraiPrimaryTarget.x,
            campY: activeSamuraiPrimaryTarget.y,
            campTargets: activeSamuraiTargets,
            attackTemplate: activeSamuraiTemplate,
            camps: {
                ...nextSamuraiCampConfig,
                campX: primarySamuraiCampTarget.x,
                campY: primarySamuraiCampTarget.y,
                campTargets: normalizedSamuraiCampTargets,
                attackTemplate: normalizedSamuraiCampTemplate
            },
            daimyo: {
                ...nextSamuraiDaimyoConfig,
                attackTrainCount: normalizedDaimyoTrainCount,
                campX: primarySamuraiDaimyoTarget.x,
                campY: primarySamuraiDaimyoTarget.y,
                campTargets: normalizedSamuraiDaimyoTargets,
                attackTemplate: normalizedSamuraiDaimyoTemplate
            }
        }
    }

    nextPlugins[NOMAD_PLUGIN_KEY].state = enabled && targetType === TARGET_TYPES.nomad
    nextPlugins[SAMURAI_PLUGIN_KEY].state = enabled && targetType === TARGET_TYPES.samurai

    nextPlugins[NOMAD_PLUGIN_KEY].useLeather = Boolean(nextConfig.nomads.useFeathers)
    nextPlugins[SAMURAI_PLUGIN_KEY].useLeather = Boolean(nextConfig.samurai.useFeathers)
    if (/\btmp,thm,tai,tcn\b/.test(String(nextPlugins.protocoltrace?.traceCommands ?? ''))) {
        nextPlugins.protocoltrace.state = false
        nextPlugins.protocoltrace.enabled = false
        nextPlugins.protocoltrace.traceCommands = ''
    }

    nextPlugins[NOMAD_PLUGIN_KEY].targetCoords = formatTargetCoords(normalizedNomadTargets)
    nextPlugins[SAMURAI_PLUGIN_KEY].targetCoords = formatTargetCoords(activeSamuraiTargets)
    nextPlugins[SAMURAI_PLUGIN_KEY].samuraiMode = nextConfig.samurai.mode

    syncAttackTemplateSelection(nextPlugins, 'camp', nextConfig.campAttackTemplate)

    nextPlugins[SAMURAI_PLUGIN_KEY].eventDifficulty =
        [SAMURAI_MODES.daimyo, SAMURAI_MODES.both].includes(nextConfig.samurai.mode) ?
            CLASSIC_SAMURAI_DIFFICULTY :
            toStringValue(nextConfig.samurai.difficulty, DEFAULT_SETTINGS.samurai.difficulty)
    nextPlugins[SAMURAI_PLUGIN_KEY].classicDaimyo32SoldiersMode = [SAMURAI_MODES.daimyo, SAMURAI_MODES.both].includes(nextConfig.samurai.mode)
    nextPlugins[SAMURAI_PLUGIN_KEY].classicDaimyoAttackTrainCount = normalizedDaimyoTrainCount
    if (activeSamuraiTemplate?.template) {
        nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateId = activeSamuraiTemplate.templateId ? Number(activeSamuraiTemplate.templateId) : 0
        nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateName = activeSamuraiTemplate.name
        nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateOwner = activeSamuraiTemplate.ownerUsername
        nextPlugins[SAMURAI_PLUGIN_KEY].attackTemplateBuilder = cloneValue(activeSamuraiTemplate.template)
    }
    else {
        delete nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateId
        delete nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateName
        delete nextPlugins[SAMURAI_PLUGIN_KEY].savedAttackTemplateOwner
        delete nextPlugins[SAMURAI_PLUGIN_KEY].attackTemplateBuilder
    }

    return nextPlugins
}

const getPrimaryFilledTarget = targets =>
    targets.find(target => target.x && target.y) ?? targets[0] ?? { x: '', y: '' }

const formatTargetCoords = targets => {
    const primaryTarget = getPrimaryFilledTarget(targets)
    return primaryTarget.x && primaryTarget.y ? `${primaryTarget.x}:${primaryTarget.y}` : ''
}

const hasSelectedTemplate = template =>
    Boolean(template?.templateId || template?.name || template?.template)

const cardSx = {
    borderRadius: '22px',
    border: '1px solid rgba(129, 146, 178, 0.18)',
    background: 'linear-gradient(180deg, rgba(21, 33, 55, 0.62) 0%, rgba(18, 29, 48, 0.5) 100%)',
    boxShadow: '0 14px 32px rgba(7, 13, 26, 0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(92, 118, 163, 0.07) 0%, rgba(92, 118, 163, 0) 22%)',
        pointerEvents: 'none'
    }
}

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{children}</Typography>
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>{label}</Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function TemplateSearchField({ label, helper, value, placeholder, ownerUsername, onOpen, onClear }) {
    return (
        <Box>
            <FieldTitle>{label}</FieldTitle>
            <TextField
                fullWidth
                value={value}
                placeholder={placeholder}
                onClick={onOpen}
                sx={inputSx}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={onOpen} edge="end" sx={{ color: '#d9e4fb' }}>
                                <SearchRoundedIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            {ownerUsername ? (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 1 }}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: '#94a4c3' }} />
                    <Typography sx={{ color: '#94a4c3', fontSize: '0.9rem' }}>
                        {`Saved by ${ownerUsername}`}
                    </Typography>
                </Stack>
            ) : null}
            {onClear && value ? (
                <Button
                    variant="outlined"
                    onClick={onClear}
                    sx={{
                        mt: 1.2,
                        minHeight: 34,
                        borderRadius: '10px',
                        borderColor: 'rgba(112, 136, 174, 0.34)',
                        color: '#eef4ff',
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': {
                            borderColor: 'rgba(112, 136, 174, 0.52)',
                            backgroundColor: 'rgba(50, 63, 88, 0.38)'
                        }
                    }}
                >
                    Clear selected template
                </Button>
            ) : null}
            <Helper>{helper}</Helper>
        </Box>
    )
}

function TemplatePickerDialog({ open, templates, onClose, onSelect }) {
    const [searchTerm, setSearchTerm] = React.useState('')

    React.useEffect(() => {
        if (open)
            setSearchTerm('')
    }, [open])

    const filteredTemplates = React.useMemo(() => {
        const normalizedQuery = String(searchTerm ?? '').trim().toLowerCase()
        if (!normalizedQuery)
            return templates

        return templates.filter(template =>
            `${template?.name ?? ''} ${template?.description ?? ''} ${template?.ownerUsername ?? ''}`
                .toLowerCase()
                .includes(normalizedQuery)
        )
    }, [searchTerm, templates])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 720px)',
                    maxWidth: '720px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(4, 10, 20, 0.62)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                        Select Attack Template
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <TextField
                    fullWidth
                    placeholder="Search by name or user"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            minHeight: 44,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(17, 26, 44, 0.92)'
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Box sx={{ maxHeight: 'min(52vh, 520px)', overflowY: 'auto', pr: 0.5 }}>
                    {filteredTemplates.length > 0 ? (
                        <Stack spacing={1.3}>
                            {filteredTemplates.map(template => (
                                <Box
                                    key={template.id}
                                    onClick={() => onSelect(template)}
                                    sx={{
                                        p: 1.7,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        cursor: 'pointer',
                                        transition: 'background-color 160ms ease, border-color 160ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            borderColor: 'rgba(94, 157, 255, 0.36)'
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f2f6ff' }}>
                                        {template?.name || 'Untitled'}
                                    </Typography>
                                    <Typography sx={{ mt: 0.65, color: '#a8b4cc', fontSize: '0.94rem' }}>
                                        {template?.description || 'No description'}
                                    </Typography>
                                    <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mt: 1.1 }}>
                                        <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: '#94a4c3' }} />
                                        <Typography sx={{ color: '#94a4c3', fontSize: '0.9rem' }}>
                                            {`Saved by ${template?.ownerUsername || 'Unknown'}`}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                p: 2.2,
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.03)'
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: '#f2f6ff' }}>
                                No saved attack templates yet.
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#a8b4cc' }}>
                                Create templates in Attack Templates first, then select one here for Samurai.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function CampTargetsEditor({ title, targets, onChange, helper }) {
    return (
        <Box>
            <SectionHeading>{title}</SectionHeading>
            <Stack spacing={2.15}>
                {targets.map((campTarget, index) => (
                    <Box
                        key={`${title.toLowerCase().replace(/\s+/g, '-')}-target-${index}`}
                        sx={{
                            p: 1.35,
                            borderRadius: '14px',
                            backgroundColor: 'rgba(31, 43, 67, 0.42)',
                            border: '1px solid rgba(129, 146, 178, 0.14)'
                        }}
                    >
                        <FieldTitle>{`${title} ${index + 1}`}</FieldTitle>
                        <Grid container spacing={2.2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={campTarget.x}
                                    placeholder="X Coordinate"
                                    onChange={event => onChange(index, 'x', event.target.value)}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    value={campTarget.y}
                                    placeholder="Y Coordinate"
                                    onChange={event => onChange(index, 'y', event.target.value)}
                                    sx={inputSx}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function EventOptionsSection({ title, config, onUpdate, skipConfig = config, onSkipUpdate = onUpdate }) {
    const updateAdvisor = producer =>
        onUpdate(nextSection => {
            nextSection.advisor = {
                ...DEFAULT_ADVISOR_SETTINGS,
                ...(nextSection.advisor ?? {})
            }
            producer(nextSection.advisor)
        })

    return (
        <Box>
            <SectionHeading>{title}</SectionHeading>
            <Grid container spacing={2.8}>
                <Grid item xs={12} md={6}>
                    <ToggleField
                        checked={config.useFeathers}
                        onChange={checked => onUpdate(nextSection => { nextSection.useFeathers = checked })}
                        label={`${title}: Use Feathers`}
                        helper={`Whether to use feathers for ${title.toLowerCase()} attacks`}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Primary Time Skip Type`}</FieldTitle>
                    <TextField
                        select
                        fullWidth
                        value={skipConfig.primaryTimeSkipType}
                        onChange={event => onSkipUpdate(nextSection => { nextSection.primaryTimeSkipType = event.target.value })}
                        sx={inputSx}
                    >
                        {TIME_SKIP_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Helper>{`Primary time skip type for ${title.toLowerCase()} attacks`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Primary Time Skip Amount`}</FieldTitle>
                    <TextField
                        fullWidth
                        value={skipConfig.primaryTimeSkipAmount}
                        onChange={event => onSkipUpdate(nextSection => { nextSection.primaryTimeSkipAmount = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <Helper>{`Primary time skip amount for ${title.toLowerCase()} attacks`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Secondary Time Skip Type`}</FieldTitle>
                    <TextField
                        select
                        fullWidth
                        value={skipConfig.secondaryTimeSkipType}
                        onChange={event => onSkipUpdate(nextSection => { nextSection.secondaryTimeSkipType = event.target.value })}
                        sx={inputSx}
                    >
                        {TIME_SKIP_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Helper>{`Secondary time skip type for ${title.toLowerCase()} attacks`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Secondary Time Skip Amount`}</FieldTitle>
                    <TextField
                        fullWidth
                        value={skipConfig.secondaryTimeSkipAmount}
                        onChange={event => onSkipUpdate(nextSection => { nextSection.secondaryTimeSkipAmount = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <Helper>{`Secondary time skip amount for ${title.toLowerCase()} attacks`}</Helper>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)', my: 0.4 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ToggleField
                        checked={config.advisor.enabled}
                        onChange={checked => updateAdvisor(nextAdvisor => { nextAdvisor.enabled = checked })}
                        label={`${title}: Use Attack Advisor`}
                        helper={`Uses the native ${title.toLowerCase()} advisor for consecutive attacks. Samurai Advisor applies to Samurai Camps, not Classic Daimyo.`}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Advisor Attack Count`}</FieldTitle>
                    <TextField
                        fullWidth
                        disabled={!config.advisor.enabled}
                        value={config.advisor.attackCount}
                        onChange={event => updateAdvisor(nextAdvisor => { nextAdvisor.attackCount = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric', min: 2, max: 9999 }}
                    />
                    <Helper>Consecutive attacks requested for each advisor movement (2-9999).</Helper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ToggleField
                        checked={config.advisor.autoActivate}
                        onChange={checked => updateAdvisor(nextAdvisor => { nextAdvisor.autoActivate = checked })}
                        label="Auto Activate"
                        helper="Consumes the event-provided advisor token when the advisor is not active."
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ToggleField
                        checked={config.advisor.autoSkipCooldown}
                        onChange={checked => updateAdvisor(nextAdvisor => { nextAdvisor.autoSkipCooldown = checked })}
                        label="Auto Skip Cooldown"
                        helper="Lets the native advisor automatically spend minute skips between consecutive hits."
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ToggleField
                        checked={config.advisor.sendBattleReports}
                        onChange={checked => updateAdvisor(nextAdvisor => { nextAdvisor.sendBattleReports = checked })}
                        label="Battle Reports"
                        helper="Creates battle inbox reports for advisor attacks. Large trains generate many messages."
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

function TimeSkipOptionsSection({ title, config, onUpdate }) {
    return (
        <Box>
            <SectionHeading>{title}</SectionHeading>
            <Grid container spacing={2.8}>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Primary Time Skip Type`}</FieldTitle>
                    <TextField
                        select
                        fullWidth
                        value={config.primaryTimeSkipType}
                        onChange={event => onUpdate(nextSection => { nextSection.primaryTimeSkipType = event.target.value })}
                        sx={inputSx}
                    >
                        {TIME_SKIP_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Helper>{`Primary time skip type used only for ${title.toLowerCase()}`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Primary Time Skip Amount`}</FieldTitle>
                    <TextField
                        fullWidth
                        value={config.primaryTimeSkipAmount}
                        onChange={event => onUpdate(nextSection => { nextSection.primaryTimeSkipAmount = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <Helper>{`Primary time skip amount used only for ${title.toLowerCase()}`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Secondary Time Skip Type`}</FieldTitle>
                    <TextField
                        select
                        fullWidth
                        value={config.secondaryTimeSkipType}
                        onChange={event => onUpdate(nextSection => { nextSection.secondaryTimeSkipType = event.target.value })}
                        sx={inputSx}
                    >
                        {TIME_SKIP_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Helper>{`Secondary time skip type used only for ${title.toLowerCase()}`}</Helper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FieldTitle>{`${title}: Secondary Time Skip Amount`}</FieldTitle>
                    <TextField
                        fullWidth
                        value={config.secondaryTimeSkipAmount}
                        onChange={event => onUpdate(nextSection => { nextSection.secondaryTimeSkipAmount = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <Helper>{`Secondary time skip amount used only for ${title.toLowerCase()}`}</Helper>
                </Grid>
            </Grid>
        </Box>
    )
}

export default function CampBotSettingsPanel({ userPlugins, onChange, templatePlatform = 'gge', eventType = TARGET_TYPES.nomad }) {
    const [templates, setTemplates] = React.useState([])
    const [templatePickerTarget, setTemplatePickerTarget] = React.useState('')
    const config = React.useMemo(() => normalizeCampBotSettings(userPlugins), [userPlugins])
    const visibleEventType = eventType === TARGET_TYPES.samurai ? TARGET_TYPES.samurai : TARGET_TYPES.nomad
    const showNomads = visibleEventType === TARGET_TYPES.nomad
    const showSamurai = visibleEventType === TARGET_TYPES.samurai
    const samuraiCampsActive = [SAMURAI_MODES.camps, SAMURAI_MODES.both].includes(config.samurai.mode)
    const samuraiDaimyoActive = [SAMURAI_MODES.daimyo, SAMURAI_MODES.both].includes(config.samurai.mode)

    const commit = React.useCallback(nextConfig => {
        onChange(buildCampBotPluginState(userPlugins, nextConfig))
    }, [onChange, userPlugins])

    const update = producer => {
        const nextConfig = cloneValue(config)
        producer(nextConfig)
        commit(nextConfig)
    }

    const updateEventSection = (sectionKey, producer) => {
        update(nextConfig => {
            producer(nextConfig[sectionKey])
        })
    }

    const updateTargets = (sectionKey, index, field, value) => {
        update(nextConfig => {
            nextConfig[sectionKey].campTargets[index] ??= { x: '', y: '' }
            nextConfig[sectionKey].campTargets[index][field] = value
        })
    }

    const updateSamuraiTargets = (modeKey, index, field, value) => {
        update(nextConfig => {
            nextConfig.samurai[modeKey] ??= createDefaultSamuraiModeSettings()
            nextConfig.samurai[modeKey].campTargets[index] ??= { x: '', y: '' }
            nextConfig.samurai[modeKey].campTargets[index][field] = value
        })
    }

    const updateSamuraiModeSection = (modeKey, producer) => {
        update(nextConfig => {
            nextConfig.samurai[modeKey] ??= createDefaultSamuraiModeSettings()
            producer(nextConfig.samurai[modeKey])
        })
    }

    React.useEffect(() => {
        let cancelled = false

        fetch(buildSavedAttackTemplatesUrl(templatePlatform), { headers: { Accept: 'application/json' } })
            .then(response => response.ok ? response.json() : { templates: [] })
            .then(payload => {
                if (cancelled)
                    return

                const nextTemplates = [...(Array.isArray(payload?.templates) ? payload.templates : [])]
                    .sort((left, right) =>
                        Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) ||
                        String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
                    )

                setTemplates(nextTemplates)
            })
            .catch(() => {
                if (!cancelled)
                    setTemplates([])
            })

        return () => {
            cancelled = true
        }
    }, [templatePlatform])

    return (
        <Box sx={cardSx}>
            <Stack spacing={4.1} sx={{ p: { xs: 2.3, md: 3.3 }, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={1.15} alignItems="flex-start">
                    <TerrainRoundedIcon sx={{ color: '#f5f7ff', mt: 0.22 }} />
                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.08rem', fontWeight: 800 }}>
                            {showNomads ? 'Nomads Bot Settings' : 'Samurai Bot Settings'}
                        </Typography>
                        <Typography sx={{ color: '#9fb0cf', fontSize: '0.98rem', mt: 0.4 }}>
                            {showNomads ?
                                'Configure only Nomad camps, targets, skips, feathers, and attack templates.' :
                                'Configure only Samurai camps and Classic Daimyo profiles, targets, skips, and attack templates.'}
                        </Typography>
                    </Box>
                </Stack>

                <ToggleField
                    checked={config.enabled && config.targetType === visibleEventType}
                    onChange={checked => update(nextConfig => {
                        nextConfig.enabled = checked
                        nextConfig.targetType = visibleEventType
                    })}
                    label={showNomads ? 'Enable Nomads Bot' : 'Enable Samurai Bot'}
                    helper={`Turns on Camp Bot using only the ${showNomads ? 'Nomads' : 'Samurai'} configuration shown here.`}
                />

                <Box>
                    <FieldTitle>Attack Count Threshold</FieldTitle>
                    <TextField
                        fullWidth
                        value={config.attackCountThreshold}
                        onChange={event => update(nextConfig => { nextConfig.attackCountThreshold = event.target.value })}
                        sx={inputSx}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <Helper>Number of attacks before stopping (Daily Limit is 3500 after that it will get more expensive)</Helper>
                </Box>

                <Box>
                    <SectionHeading>Sender Coordinates</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Sender X Coordinate</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.senderX}
                                onChange={event => update(nextConfig => { nextConfig.senderX = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>X coordinate of the attack sender (Keep it empty to use your main Castle)</Helper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Sender Y Coordinate</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.senderY}
                                onChange={event => update(nextConfig => { nextConfig.senderY = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>Y coordinate of the attack sender (Keep it empty to use your main Castle)</Helper>
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    <SectionHeading>Shared Camp Settings</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Kingdom ID</FieldTitle>
                            <TextField
                                select
                                fullWidth
                                value={config.kingdomId}
                                onChange={event => update(nextConfig => { nextConfig.kingdomId = event.target.value })}
                                sx={inputSx}
                            >
                                {KINGDOM_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Helper>Select the target kingdom used by your saved Camp Bot profile.</Helper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FieldTitle>Slowest Unit Speed</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.slowestUnitSpeed}
                                onChange={event => update(nextConfig => { nextConfig.slowestUnitSpeed = event.target.value })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Helper>Saved with Camp Bot so you can keep the same profile values in one place.</Helper>
                        </Grid>
                        {showNomads && <Grid item xs={12}>
                            <TemplateSearchField
                                label="Camp Attack Template"
                                helper="Linked with Attack Setup. Nomads use this template directly, and Samurai uses it as fallback when no Samurai-specific template is selected."
                                value={config.campAttackTemplate.name}
                                placeholder="Select a saved Camp attack template"
                                ownerUsername={config.campAttackTemplate.ownerUsername}
                                onOpen={() => setTemplatePickerTarget('camp')}
                                onClear={hasSelectedTemplate(config.campAttackTemplate) ? () => update(nextConfig => {
                                    nextConfig.campAttackTemplate = normalizeTemplate(EMPTY_TEMPLATE)
                                }) : undefined}
                            />
                        </Grid>}
                    </Grid>
                </Box>

                {showNomads && <>
                    <CampTargetsEditor
                        title="Nomad Camp"
                        targets={config.nomads.campTargets}
                        onChange={(index, field, value) => updateTargets('nomads', index, field, value)}
                        helper="Configure up to 4 Nomad camps. Nomads rotate through all filled slots."
                    />

                    <EventOptionsSection
                        title="Nomads"
                        config={config.nomads}
                        onUpdate={producer => updateEventSection('nomads', producer)}
                    />
                </>}

                {showSamurai && <>
                <Box>
                    <SectionHeading>Samurai Mode</SectionHeading>
                    <Grid container spacing={2.8}>
                        <Grid item xs={12} md={4}>
                            <FieldTitle>Samurai Mode</FieldTitle>
                            <TextField
                                select
                                fullWidth
                                value={config.samurai.mode}
                                onChange={event => updateEventSection('samurai', nextSection => { nextSection.mode = event.target.value })}
                                sx={inputSx}
                            >
                                <MenuItem value={SAMURAI_MODES.camps}>Samurai Camps</MenuItem>
                                <MenuItem value={SAMURAI_MODES.daimyo}>Classic Daimyo 32 Soldiers</MenuItem>
                                <MenuItem value={SAMURAI_MODES.both}>Samurai Camps + Classic Daimyo</MenuItem>
                            </TextField>
                            <Helper>Choose Samurai Camps only, Classic Daimyo only, or run both profiles together in the same Samurai session.</Helper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FieldTitle>Samurai Difficulty</FieldTitle>
                            <TextField
                                select
                                fullWidth
                                value={config.samurai.difficulty}
                                onChange={event => updateEventSection('samurai', nextSection => { nextSection.difficulty = event.target.value })}
                                sx={inputSx}
                            >
                                {SAMURAI_DIFFICULTY_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Helper>
                                {[SAMURAI_MODES.daimyo, SAMURAI_MODES.both].includes(config.samurai.mode) ?
                                    'Classic Daimyo requires Classic difficulty. In combined mode, Samurai Camps also runs under the same Classic event selection.' :
                                    'Difficulty used when Samurai Camps mode is active.'}
                            </Helper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TemplateSearchField
                                label="Samurai Camps Attack Template"
                                helper={
                                    samuraiCampsActive ?
                                        'Active now. This template is currently used by Samurai Camps.' :
                                        'Saved separately from Classic Daimyo. Switch Samurai Mode to Samurai Camps to use this template.'
                                }
                                value={config.samurai.camps.attackTemplate.name}
                                placeholder="Select a saved Samurai attack template"
                                ownerUsername={config.samurai.camps.attackTemplate.ownerUsername}
                                onOpen={() => setTemplatePickerTarget('samurai:camps')}
                                onClear={hasSelectedTemplate(config.samurai.camps.attackTemplate) ? () => update(nextConfig => {
                                    nextConfig.samurai.camps.attackTemplate = normalizeTemplate(EMPTY_TEMPLATE)
                                }) : undefined}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TemplateSearchField
                                label="Classic Daimyo Attack Template"
                                helper={
                                    samuraiDaimyoActive ?
                                        'Active now. This template is currently used by Classic Daimyo 32 Soldiers.' :
                                        'Saved separately from Samurai Camps. Switch Samurai Mode to Classic Daimyo 32 Soldiers to use this template.'
                                }
                                value={config.samurai.daimyo.attackTemplate.name}
                                placeholder="Select a saved Daimyo attack template"
                                ownerUsername={config.samurai.daimyo.attackTemplate.ownerUsername}
                                onOpen={() => setTemplatePickerTarget('samurai:daimyo')}
                                onClear={hasSelectedTemplate(config.samurai.daimyo.attackTemplate) ? () => update(nextConfig => {
                                    nextConfig.samurai.daimyo.attackTemplate = normalizeTemplate(EMPTY_TEMPLATE)
                                }) : undefined}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FieldTitle>Classic Daimyo Train Attacks</FieldTitle>
                            <TextField
                                fullWidth
                                value={config.samurai.daimyo.attackTrainCount}
                                onChange={event => update(nextConfig => {
                                    nextConfig.samurai.daimyo ??= createDefaultSamuraiModeSettings({
                                        attackTrainCount: DEFAULT_CLASSIC_DAIMYO_ATTACK_TRAIN_COUNT
                                    })
                                    nextConfig.samurai.daimyo.attackTrainCount = event.target.value
                                })}
                                sx={inputSx}
                                inputProps={{ inputMode: 'numeric', min: 1, max: 20 }}
                            />
                            <Helper>Classic Daimyo attacks to send as a train before waiting for the exact commanders to return (maximum 20).</Helper>
                        </Grid>
                    </Grid>
                </Box>

                <CampTargetsEditor
                    title="Samurai Camp Targets"
                    targets={config.samurai.camps.campTargets}
                    onChange={(index, field, value) => updateSamuraiTargets(SAMURAI_MODES.camps, index, field, value)}
                    helper={
                        samuraiCampsActive ?
                            'Active now. Samurai Camps rotates through these coordinates and keeps them fully separate from Classic Daimyo castle targets.' :
                            'Saved separately from Classic Daimyo. Switch Samurai Mode to Samurai Camps to use these targets.'
                    }
                />

                <CampTargetsEditor
                    title="Classic Daimyo Targets"
                    targets={config.samurai.daimyo.campTargets}
                    onChange={(index, field, value) => updateSamuraiTargets(SAMURAI_MODES.daimyo, index, field, value)}
                    helper={
                        samuraiDaimyoActive ?
                            'Active now. Classic Daimyo uses the first filled target as an optional manual castle seed. Leave all slots empty if you want auto-search.' :
                            'Saved separately from Samurai Camps. Switch Samurai Mode to Classic Daimyo 32 Soldiers to use these targets.'
                    }
                />

                <EventOptionsSection
                    title="Samurai Camps"
                    config={config.samurai}
                    onUpdate={producer => updateEventSection('samurai', producer)}
                    skipConfig={config.samurai.camps}
                    onSkipUpdate={producer => updateSamuraiModeSection(SAMURAI_MODES.camps, producer)}
                />

                <TimeSkipOptionsSection
                    title="Classic Daimyo"
                    config={config.samurai.daimyo}
                    onUpdate={producer => updateSamuraiModeSection(SAMURAI_MODES.daimyo, producer)}
                />
                </>}

            </Stack>

            <TemplatePickerDialog
                open={Boolean(templatePickerTarget)}
                templates={templates}
                onClose={() => setTemplatePickerTarget('')}
                onSelect={template => {
                    update(nextConfig => {
                        const normalizedTemplate = normalizeTemplate({
                            templateId: String(template?.id ?? ''),
                            name: template?.name,
                            ownerUsername: template?.ownerUsername,
                            description: template?.description,
                            template: cloneValue(template?.template ?? {})
                        })

                        if (templatePickerTarget === 'camp')
                            nextConfig.campAttackTemplate = normalizedTemplate
                        else if (templatePickerTarget === 'samurai:camps')
                            nextConfig.samurai.camps.attackTemplate = normalizedTemplate
                        else if (templatePickerTarget === 'samurai:daimyo')
                            nextConfig.samurai.daimyo.attackTemplate = normalizedTemplate
                    })
                    setTemplatePickerTarget('')
                }}
            />
        </Box>
    )
}
